import React, { useState, useEffect } from "react";
import SEO from "../seo";
const UninstallApps = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "uninstalling-an-app",
        "viewing-uninstalled-apps",
        "reinstalling-an-app",
        "viewing-app-history",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Uninstall Apps | xPage Help Center"
        description="Learn how to uninstall, reinstall, and track the history of apps on Xpage. Keep your store optimized and secure by managing installed apps efficiently."
        keywords="Xpage apps, uninstall app, reinstall app, app history, manage apps, remove apps, dashboard apps"
        canonical="https://help.xpage.ai/help/apps/Uninstall%20Apps"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Uninstall Apps 
          </h1>
          <p className="text-sm text-systemLabel">
            Xpage allows you to fully manage your apps, including uninstalling
            those you no longer need, reinstalling previously removed apps, and
            tracking your app history. This ensures your store remains
            optimized, uncluttered, and secure.
          </p>
        </header>

        {/* Uninstalling an App Section */}
        <section id="uninstalling-an-app" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Uninstalling an App
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                You can remove apps directly from your Xpage dashboard. Some
                apps may modify store templates, so it's important to review any
                potential effects before uninstalling. If you're unsure, contact
                the app developer for guidance via the app details page.
              </p>

              <p className="text-sm text-systemLabel">
                Steps to uninstall an app:
              </p>

              <div className="space-y-3">
                <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Step 1
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Log in to your Xpage dashboard and navigate to Apps.
                  </p>
                </div>
                <div className="p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Step 2
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Locate the app you want to remove, click the … (more
                    options) button, and select Uninstall App.
                  </p>
                </div>
                <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Step 3
                  </strong>
                  <p className="text-systemLabel text-sm">
                    (Optional) Choose a reason for uninstalling from the
                    dropdown menu.
                  </p>
                </div>
                <div className="p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Step 4
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Click Uninstall to remove the app from your store.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Viewing Uninstalled Apps Section */}
        <section id="viewing-uninstalled-apps" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Viewing Uninstalled Apps
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                You can easily access a list of all apps that have been removed
                from your store.
              </p>

              <p className="text-sm text-systemLabel">Steps:</p>

              <div className="space-y-3">
                <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Step 1
                  </strong>
                  <p className="text-systemLabel text-sm">
                    In your Xpage dashboard, go to Apps.
                  </p>
                </div>
                <div className="p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Step 2
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Click the Uninstalled tab to view all uninstalled apps.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Reinstalling an App Section */}
        <section id="reinstalling-an-app" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Reinstalling an App
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Apps that were previously uninstalled can be re-added to your
                store at any time. This allows you to quickly restore
                functionality without searching for the app again.
              </p>

              <p className="text-sm text-systemLabel">Steps to reinstall:</p>

              <div className="space-y-3">
                <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Step 1
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Go to the Apps page in your Xpage dashboard.
                  </p>
                </div>
                <div className="p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Step 2
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Click the Uninstalled tab and locate the app you wish to
                    reinstall.
                  </p>
                </div>
                <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Step 3
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Click Install to restore the app.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Viewing App History Section */}
        <section id="viewing-app-history" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Viewing App Installation & Uninstallation History
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Xpage keeps a detailed record of all app installation and
                removal events. This helps you track changes and identify which
                team members performed each action.
              </p>

              <p className="text-sm text-systemLabel">Steps to view history:</p>

              <div className="space-y-3">
                <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Step 1
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Go to Apps in your Xpage dashboard.
                  </p>
                </div>
                <div className="p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Step 2
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Click the Installed or Uninstalled tab to find the app.
                  </p>
                </div>
                <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Step 3
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Click the app's name to open its Details Page.
                  </p>
                </div>
                <div className="p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Step 4
                  </strong>
                  <p className="text-systemLabel text-sm">
                    In the App History section, view all installation and
                    uninstallation events.
                  </p>
                </div>
                <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Step 5
                  </strong>
                  <p className="text-systemLabel text-sm">
                    If there are more than three events, click View All to see
                    the complete timeline.
                  </p>
                </div>
                <div className="p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Step 6
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Click any event to view details, including the
                    uninstallation reason, description, and the user who
                    performed the action.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#uninstalling-an-app"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "uninstalling-an-app"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Uninstalling an App
              </a>
            </li>
            <li>
              <a
                href="#viewing-uninstalled-apps"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "viewing-uninstalled-apps"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Viewing Uninstalled Apps
              </a>
            </li>
            <li>
              <a
                href="#reinstalling-an-app"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "reinstalling-an-app"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Reinstalling an App
              </a>
            </li>
            <li>
              <a
                href="#viewing-app-history"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "viewing-app-history"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Viewing App History
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default UninstallApps;
